import React, { useState, useCallback, useEffect } from "react"
import { CircularProgress, AutocompleteProps } from "@mui/material"
import {
  FilterCondition,
  FilterOperator,
  LogicalOperator,
  SortDirection,
  useGetLocationListForOptionsLazyQuery,
  Vendor
} from "src/graphql/quark/quark.type"
import InputAutocomplete from "src/shared/components/input-autocomplete"
import { useTypedSelector } from "src/shared/store/store"
import { WatchQueryFetchPolicy } from "@apollo/client"

interface Option {
  id: string
  name: string
  type: string
}

const SelectLocationField: React.FC<
  {
    onChange: (val: string | string[]) => void
    value?: string | string[] | null
    multiple?: boolean
    error?: boolean
    showChip?: "outside" | "inside"
    helperText?: string
    vendor?: Vendor
    label?: string
    placeholder?: string
    required?: boolean
    startAdornment?: React.ReactNode
    fetchPolicy?: WatchQueryFetchPolicy
    isAllowedLocationOnly?: boolean
    filterOptionsQuery?: FilterCondition[]
    exceptCurrentLocation?: boolean
  } & Omit<AutocompleteProps<any, any, any, any>, "renderInput" | "options">
> = ({
  onChange,
  value,
  error,
  multiple,
  helperText,
  vendor,
  showChip,
  label,
  placeholder,
  required,
  startAdornment,
  fetchPolicy,
  isAllowedLocationOnly,
  exceptCurrentLocation,
  filterOptionsQuery,
  ...rest
}) => {
    const auth = useTypedSelector((state) => state.authentication)
    const [options, setOptions] = useState<Option[]>([])
    const [loading, setLoading] = useState(false)
    const [getLocations, { error: getError }] = useGetLocationListForOptionsLazyQuery()
    console.log("location-field-value-nside", { value, options })
    useEffect(() => {
      if (value && !options.length) fetchOptions()
    }, [value])
    const fetchOptions = useCallback(async () => {
      if (isAllowedLocationOnly) {
        let tempOptions = (
          auth?.allowedLocations?.map((val: any) => ({
            id: val.id,
            name: val.name,
            type: val.type
          })) || []
        ).sort((a, b) => -a.type.localeCompare(b.type))

        if (exceptCurrentLocation) {
          tempOptions = tempOptions.filter((val) => val.id !== auth.currentWhLoc?.id)
        }
        setOptions(tempOptions)
        return
      }
      setLoading(true)
      try {
        const response = await getLocations({
          fetchPolicy: fetchPolicy,
          variables: {
            locationId: auth.currentWhLoc?.id as string,
            filter: {
              conditions: [
                {
                  operator: FilterOperator.Equal,
                  field: "status",
                  value: "ACTIVE"
                },
                ...(filterOptionsQuery || [])
              ],
              logicalOperator: LogicalOperator.And
            },
            sort: [
              {
                direction: SortDirection.Asc,
                field: "name"
              }
            ],
            page: 1,
            limit: 100000
          }
        })

        if (!response.data?.getLocations.data.length) return null
        let resData: Option[] = response.data?.getLocations.data
          ?.map((val: any) => ({
            id: val.id as string,
            name: val.name as string,
            type: val.type as string
          }))
          ?.sort((a, b) => -a.type.localeCompare(b.type))
        if (exceptCurrentLocation) {
          resData = resData.filter((val) => val.id !== auth.currentWhLoc?.id)
        }

        setOptions(resData)
      } catch (error) {
        console.error("Error fetching data:", error)
      } finally {
        setLoading(false)
      }
    }, [loading, isAllowedLocationOnly, auth?.allowedLocations])

    return (
      <InputAutocomplete
        {...rest}
        options={options}
        multiple={multiple}
        getOptionKey={(option) => option?.id ?? ""}
        getOptionLabel={(option) => option.name ?? ""}
        onOpen={() => fetchOptions()}
        loading={loading}
        value={multiple ? options.filter((val) => value?.includes(val.id)) : options.find((val) => val.id === value)}
        onChange={(val: any) => {
          onChange(multiple ? val?.map((val: any) => val.id) : val?.id)
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        )}
        groupBy={(option) => option.type}
        showChip={showChip}
        label={label}
        placeholder={placeholder}
        required={required}
        startAdornment={startAdornment}
        loadingText={<CircularProgress size={20} />}
        error={error ?? !!getError?.message}
        helperText={helperText ?? getError?.message}
      />
    )
  }

export default SelectLocationField
